import { useEffect, useState } from 'react'
import { useForm, useWatch } from 'react-hook-form'
import apiService from 'services/api'
import { toast } from 'react-toastify'
import { z } from 'zod'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import * as S from './CompanyAttrsManages.styles'
import FbButton from '../FbUI/FbButton'
import dialogConfirm from 'components/dialogConfirm'
import { CreatableTagSelector } from 'components/CreatableTagSelector'
import { zodResolver } from '@hookform/resolvers/zod'
import { CreatableDistributorSelector } from 'components/CreatableDistributorSelector'
import { CreatableGPOSelector } from 'components/CreatableGPOSelector'
import { NoteManager } from 'components/Notes/NoteManager'
import clsx from 'clsx'
import { Tag } from 'models/tags'
import { CompanyDistributor } from 'models/companies'
import { CompanyGPO } from 'models/companies'

const notesShape = z.object({
  taglist: z.any().optional().nullable(),
  distributors: z.any().optional().nullable(),
  gpos: z.any().optional().nullable(),
  note: z.string().optional().nullable(),
  attachments: z.any().optional().nullable(),
  previous_taglist: z.any().optional().nullable(),
  previous_distributors: z.any().optional().nullable(),
  previous_gpos: z.any().optional().nullable(),
})
export type CompanyAttrsInput = z.infer<typeof notesShape>

export function CompanyAttributesManager({
  chainID,
  contactCompanyID,
  doorProxyId,
  isDoorPage,
}: {
  chainID?: number
  contactCompanyID?: number
  doorProxyId?: number
  isDoorPage?: boolean
}) {
  const queryClient = useQueryClient()
  const api = apiService()
  const { data: distributorsOptions } = api.useGetCompanyDistributorsOptions()
  const { data: gposOptions } = api.useGetCompanyGPOsOptions()
  const { data: tagsOptions } = api.useGetTagsOptions()
  const { data: chainData, refetch: refetchChain } = useQuery({
    queryKey: ['chain', chainID],
    queryFn: () => api.getChain(chainID),
    enabled: !!chainID && !doorProxyId,
  })
  const { data: contactCompanyData, refetch: refetchContactCompany } = useQuery(
    {
      queryKey: ['contactCompany', contactCompanyID],
      queryFn: () => api.getCompany(contactCompanyID!),
      enabled: !!contactCompanyID && !doorProxyId,
    }
  )

  const { data: doorProxyData, refetch: refetchDoorProxy } = useQuery({
    queryKey: ['doorProxy', doorProxyId],
    queryFn: () => api.getDoorProxy(doorProxyId!),
    enabled: !!doorProxyId,
  })

  const [previousDistributors, setPreviousDistributors] = useState<number[]>([])
  const [previousGPOs, setPreviousGPOs] = useState<number[]>([])
  const [previousTaglist, setPreviousTaglist] = useState<number[]>([])
  const [isDoor, setIsDoor] = useState(false)

  const { control, handleSubmit, reset, setError, formState } =
    useForm<CompanyAttrsInput>({
      defaultValues: {
        taglist: undefined,
        distributors: undefined,
        gpos: undefined,
        note: '',
        attachments: [],
      },
      resolver: zodResolver(notesShape),
    })

  // Watch note and attachments to determine if we should disable save
  const noteText = useWatch({ control, name: 'note' })
  const attachments = useWatch({ control, name: 'attachments' })

  useEffect(() => {
    // Check if we are dealing with a door proxy
    setIsDoor(!!doorProxyId)

    if (chainData || contactCompanyData || doorProxyData) {
      // Extract tags, distributors, and GPOs from the appropriate data source
      let taglist: Tag[] = []
      let distributors: CompanyDistributor[] = []
      let gpos: CompanyGPO[] = []

      if (doorProxyData) {
        // Use door proxy data if available
        taglist = doorProxyData.taglist || []
        distributors = doorProxyData.distributors || []
        gpos = doorProxyData.gpos || []
      } else if (chainData) {
        // Use chain data next
        taglist = chainData.taglist || []
        distributors = chainData.distributors || []
        gpos = chainData.gpos || []
      } else if (contactCompanyData) {
        // Fall back to contact company data
        taglist = contactCompanyData.taglist || []
        distributors = contactCompanyData.distributors || []
        gpos = contactCompanyData.gpos || []
      }

      const mappedTaglist = taglist.map((t) => t.id) || []
      const mappedDistributors =
        distributors.map((distributor) => distributor.id) || []
      const mappedGPOs = gpos.map((gpo) => gpo.id) || []

      reset({
        taglist: mappedTaglist,
        distributors: mappedDistributors,
        gpos: mappedGPOs,
        note: '',
        attachments: [],
      })

      setPreviousDistributors(mappedDistributors)
      setPreviousGPOs(mappedGPOs)
      setPreviousTaglist(mappedTaglist)
    }
  }, [chainData, contactCompanyData, doorProxyData, reset, doorProxyId])

  const mutation = useMutation({
    mutationFn: async (values: CompanyAttrsInput) => {
      if (doorProxyId) {
        // Handle door proxy attributes update
        await api.bulkEditDoorAttrs({
          door_proxy_ids: [doorProxyId],
          taglist:
            Array.isArray(values.taglist) && values.taglist.length > 0
              ? values?.taglist
              : 'REMOVE',
          distributors:
            Array.isArray(values.distributors) && values.distributors.length > 0
              ? values.distributors
              : 'REMOVE',
          gpos:
            Array.isArray(values.gpos) && values.gpos.length > 0
              ? values.gpos
              : 'REMOVE',
          previous_distributors: previousDistributors,
          previous_gpos: previousGPOs,
          previous_taglist: previousTaglist,
        })
      } else {
        // Existing functionality for chains/companies
        await api.bulkEditCompanyAttrs({
          chain_ids: chainID ? [chainID] : undefined,
          contact_company_ids: !chainID ? [contactCompanyID!] : undefined,
          taglist:
            Array.isArray(values.taglist) && values.taglist.length > 0
              ? values?.taglist
              : 'REMOVE',
          distributors:
            Array.isArray(values.distributors) && values.distributors.length > 0
              ? values.distributors
              : 'REMOVE',
          gpos:
            Array.isArray(values.gpos) && values.gpos.length > 0
              ? values.gpos
              : 'REMOVE',
          previous_distributors: previousDistributors,
          previous_gpos: previousGPOs,
          previous_taglist: previousTaglist,
        })
      }

      // Only save note if there is text content - attachments alone are not sufficient
      if (values.note?.trim()) {
        await api.createNote(
          chainID,
          contactCompanyID,
          values.note,
          values.attachments || [],
          doorProxyId
        )
      }
    },

    onSettled: async (data, err) => {
      if (doorProxyId) {
        refetchDoorProxy()
      } else if (chainID) {
        refetchChain()
      } else {
        refetchContactCompany()
      }
      queryClient.invalidateQueries({ queryKey: ['notes-table'] })
      if (err) {
        toast.error('Error updating.')
      } else {
        toast.success(doorProxyId ? 'Door updated.' : 'Company updated.')
        reset({ ...formState.defaultValues, note: '', attachments: [] })
      }
    },
  })

  const onSubmit = handleSubmit(async (values) => {
    if (chainID && !doorProxyId) {
      if (
        !isDoorPage ||
        (await dialogConfirm(
          'Changing information on a door will apply to all doors in a chain. Would you like to proceed?',
          'Heads Up'
        ))
      ) {
        mutation.mutate(values)
      }
    } else {
      mutation.mutate(values)
    }
  })

  return (
    <>
      <S.Wrapper>
        <CreatableTagSelector
          name="taglist"
          control={control}
          label={isDoor ? 'Door Tags' : 'Company Tags'}
          placeholder="Tags"
          options={tagsOptions}
          setError={setError}
        />
        <CreatableDistributorSelector
          name="distributors"
          control={control}
          label={isDoor ? 'Door Distributors' : 'Company Distributors'}
          placeholder="Distributor(s)"
          options={distributorsOptions}
          setError={setError}
        />
        <CreatableGPOSelector
          name="gpos"
          control={control}
          label={
            isDoor
              ? 'Door Group Purchasing Organizations'
              : 'Company Group Purchasing Organizations'
          }
          placeholder="GPO(s)"
          options={gposOptions}
          setError={setError}
        />
        <NoteManager
          noteName="note"
          attachmentsName="attachments"
          control={control}
          label={isDoor ? 'Door Note' : 'Company Note'}
          placeholder="Start writing note here"
          enableMentions={true}
        />
      </S.Wrapper>
      <FbButton
        className={clsx('ml-auto', formState.isDirty ? 'block' : 'invisible')}
        loading={mutation.isPending}
        onClick={onSubmit}
        disabled={!noteText?.trim() && attachments?.length > 0}
        tooltip={
          !noteText?.trim() && attachments?.length
            ? 'Add text to save note'
            : ''
        }
      >
        Save
      </FbButton>
    </>
  )
}
